import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Clock, BookOpen, Bell, Trash2, Edit2, 
  CheckCircle2, Plus, Loader2, Users, Heart, Repeat, Play
} from 'lucide-react';

// Story emoji mapping
const STORY_EMOJIS = {
  'creation': '🌍', 'adam-eve': '🍎', 'noah': '🚢', 'rainbow-promise': '🌈',
  'abraham': '⭐', 'joseph': '🧥', 'moses': '🔥', 'red-sea': '🌊',
  'david': '👑', 'goliath': '⚔️', 'daniel': '🦁', 'jonah': '🐋',
  'esther': '👸', 'jesus': '✝️', 'christmas': '🎄', 'resurrection': '🌅'
};

const getStoryEmoji = (storyId) => {
  for (const [key, emoji] of Object.entries(STORY_EMOJIS)) {
    if (storyId?.toLowerCase().includes(key)) return emoji;
  }
  return '📖';
};

const FamilyStoryTime = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api, token } = useAuth();
  
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(childId || '');
  const [storyTimes, setStoryTimes] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    story_id: '',
    scheduled_date: '',
    scheduled_time: '19:00',
    recurrence: 'once',
    reminder_minutes: 15
  });

  useEffect(() => {
    fetchData();
  }, [selectedChild]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch children
      const childrenRes = await api.get('/children');
      setChildren(childrenRes.data || []);
      
      if (!selectedChild && childrenRes.data?.length > 0) {
        setSelectedChild(childrenRes.data[0].id);
      }
      
      // Fetch story times
      const storyTimesRes = await api.get(`/family-story-time${selectedChild ? `?child_id=${selectedChild}` : ''}`);
      setStoryTimes(storyTimesRes.data.story_times || []);
      
      // Fetch available stories
      const storiesRes = await api.get('/bible/stories/1'); // Level 1 stories
      setStories(storiesRes.data.stories || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load story times');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleForm.story_id || !scheduleForm.scheduled_date || !selectedChild) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setScheduling(true);
    try {
      const scheduledDateTime = new Date(`${scheduleForm.scheduled_date}T${scheduleForm.scheduled_time}:00`);
      
      await api.post('/family-story-time', {
        child_id: selectedChild,
        story_id: scheduleForm.story_id,
        scheduled_time: scheduledDateTime.toISOString(),
        recurrence: scheduleForm.recurrence,
        reminder_minutes: scheduleForm.reminder_minutes
      });
      
      toast.success('Story time scheduled! 📚');
      setShowScheduleDialog(false);
      setScheduleForm({
        story_id: '',
        scheduled_date: '',
        scheduled_time: '19:00',
        recurrence: 'once',
        reminder_minutes: 15
      });
      fetchData();
    } catch (error) {
      console.error('Failed to schedule:', error);
      toast.error('Failed to schedule story time');
    } finally {
      setScheduling(false);
    }
  };

  const handleDelete = async (storyTimeId) => {
    if (!confirm('Are you sure you want to cancel this story time?')) return;
    
    try {
      await api.delete(`/api/family-story-time/${storyTimeId}`);
      toast.success('Story time cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel story time');
    }
  };

  const handleComplete = async (storyTimeId) => {
    try {
      await api.post(`/api/family-story-time/${storyTimeId}/complete`);
      toast.success('Story time completed! ⭐');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as complete');
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    };
  };

  const isUpcoming = (isoString) => {
    return new Date(isoString) > new Date();
  };

  const getRecurrenceLabel = (recurrence) => {
    switch (recurrence) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      default: return 'One-time';
    }
  };

  const upcomingStoryTimes = storyTimes.filter(st => isUpcoming(st.scheduled_time) && !st.completed);
  const pastStoryTimes = storyTimes.filter(st => !isUpcoming(st.scheduled_time) || st.completed);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white p-6 pb-8 rounded-b-3xl shadow-lg">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <span className="text-4xl">📖</span>
                Family Story Time
              </h1>
              <p className="text-pink-100">Schedule special moments with your little ones</p>
            </div>
            <Button 
              onClick={() => setShowScheduleDialog(true)}
              className="bg-white text-purple-600 hover:bg-purple-50"
              data-testid="schedule-story-time-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Schedule Story Time
            </Button>
          </div>
          
          {/* Child Selector */}
          {children.length > 1 && (
            <div className="mt-4">
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger className="w-48 bg-white/20 border-white/30 text-white">
                  <SelectValue placeholder="Select child" />
                </SelectTrigger>
                <SelectContent>
                  {children.map(child => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 md:p-6 -mt-4">
        {/* Stats Banner */}
        <Card className="bg-gradient-to-r from-amber-100 to-orange-100 border-0 mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-amber-700">{upcomingStoryTimes.length}</div>
                <div className="text-sm text-amber-600">Upcoming</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-700">{pastStoryTimes.filter(st => st.completed).length}</div>
                <div className="text-sm text-green-600">Completed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-700">{storyTimes.filter(st => st.recurrence !== 'once').length}</div>
                <div className="text-sm text-purple-600">Recurring</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Story Times */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-purple-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Upcoming Story Times
          </h2>
          
          {upcomingStoryTimes.length === 0 ? (
            <Card className="border-dashed border-2 border-purple-200">
              <CardContent className="p-8 text-center">
                <div className="text-5xl mb-4">📅</div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No story times scheduled</h3>
                <p className="text-slate-500 mb-4">Create special reading moments with your family!</p>
                <Button onClick={() => setShowScheduleDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Your First Story Time
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {upcomingStoryTimes.map((storyTime, index) => {
                  const { date, time } = formatDateTime(storyTime.scheduled_time);
                  return (
                    <motion.div
                      key={storyTime.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="hover:shadow-lg transition-shadow" data-testid={`story-time-${storyTime.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="text-4xl">{getStoryEmoji(storyTime.story_id)}</div>
                            <div className="flex-1">
                              <h3 className="font-bold text-slate-800">{storyTime.story_title}</h3>
                              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {date}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {time}
                                </span>
                                {storyTime.recurrence !== 'once' && (
                                  <Badge variant="outline" className="text-purple-600 border-purple-300">
                                    <Repeat className="w-3 h-3 mr-1" />
                                    {getRecurrenceLabel(storyTime.recurrence)}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                                <Bell className="w-3 h-3" />
                                Reminder {storyTime.reminder_minutes} min before
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Link to={`/bible-story/${storyTime.story_id}?child=${storyTime.child_id}`}>
                                <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                                  <Play className="w-4 h-4 mr-1" />
                                  Start
                                </Button>
                              </Link>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => handleComplete(storyTime.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDelete(storyTime.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Past Story Times */}
        {pastStoryTimes.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-600 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Past Story Times
            </h2>
            <div className="space-y-3">
              {pastStoryTimes.slice(0, 5).map(storyTime => {
                const { date, time } = formatDateTime(storyTime.scheduled_time);
                return (
                  <Card key={storyTime.id} className="bg-slate-50 opacity-70">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="text-3xl opacity-50">{getStoryEmoji(storyTime.story_id)}</div>
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-600">{storyTime.story_title}</h3>
                          <div className="text-sm text-slate-400">{date} at {time}</div>
                        </div>
                        {storyTime.completed && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">📖</span>
              Schedule Story Time
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Story Selection */}
            <div>
              <Label>Choose a Story *</Label>
              <Select 
                value={scheduleForm.story_id} 
                onValueChange={(v) => setScheduleForm({...scheduleForm, story_id: v})}
              >
                <SelectTrigger className="mt-1" data-testid="story-select">
                  <SelectValue placeholder="Select a story..." />
                </SelectTrigger>
                <SelectContent>
                  {stories.map(story => (
                    <SelectItem key={story.id} value={story.id}>
                      {getStoryEmoji(story.id)} {story.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date */}
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={scheduleForm.scheduled_date}
                onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
                data-testid="schedule-date"
              />
            </div>
            
            {/* Time */}
            <div>
              <Label>Time *</Label>
              <Input
                type="time"
                value={scheduleForm.scheduled_time}
                onChange={(e) => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})}
                className="mt-1"
                data-testid="schedule-time"
              />
            </div>
            
            {/* Recurrence */}
            <div>
              <Label>Repeat</Label>
              <Select 
                value={scheduleForm.recurrence} 
                onValueChange={(v) => setScheduleForm({...scheduleForm, recurrence: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">One-time only</SelectItem>
                  <SelectItem value="daily">Every day</SelectItem>
                  <SelectItem value="weekly">Every week</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Reminder */}
            <div>
              <Label>Remind me before</Label>
              <Select 
                value={String(scheduleForm.reminder_minutes)} 
                onValueChange={(v) => setScheduleForm({...scheduleForm, reminder_minutes: parseInt(v)})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSchedule} 
              disabled={scheduling}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-schedule-btn"
            >
              {scheduling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scheduling...</>
              ) : (
                <><Calendar className="w-4 h-4 mr-2" /> Schedule</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyStoryTime;
